import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { Sensor } from 'src/api'
import { useApiInstance } from './useApiInstance'
import { buildRequestParams, ObjectType, transformPage, transformPerPage } from './helpers'

interface RequestParams {
  params?: Partial<AdminThingspaceSensorsGetParams>
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>
  options?: Omit<UseQueryOptions<unknown, Error, APIList<Sensor>>, 'queryKey' | 'queryFn'>
}

interface AdminThingspaceSensorsGetParams {
  limit: number
  offset: number
  sort: string
  search: string
  isAvailableInSystem: string
  activatedMoreThanMonths: number
  noPingMoreThanMonths: number
  activationStatus: string
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  search: 'string',
  isAvailableInSystem: 'string',
  activatedMoreThanMonths: 'number',
  noPingMoreThanMonths: 'number',
  activationStatus: 'string'
}

export const useThingspaceSensorsListRequest = ({ params = {}, paramsTransform = [], options }: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [transformPerPage, transformPage, ...paramsTransform].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminThingspaceSensorsGetParams>(transformedParams, schema)

  return useQuery<unknown, Error, APIList<Sensor>>(
    [Entities.Sensors, JSON.stringify(transformedParams)],
    async () => {
      const { limit, offset, sort, search, isAvailableInSystem, activatedMoreThanMonths, noPingMoreThanMonths, activationStatus } = queryParams
      const { data } = await api.v1AdminSensorsThingspaceSensorsGet(
        limit,
        offset,
        sort,
        search,
        isAvailableInSystem,
        activatedMoreThanMonths,
        noPingMoreThanMonths,
        activationStatus
      )
      return data
    },
    options
  )
}
